import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import { getRequest, postRequest, putRequest } from 'services/apiRequests';
import { CSRFService } from 'services/csrf';
import { spmActivityKeys } from 'services/threads';
import type { APIList } from 'types';
import type { ApprovalGroupDetail, SPMApproval, SPMWorkflowSummary } from 'types/SPM';
import type { APIError } from 'utils/errors';

export type { SPMApproval, SPMApprovalGroup, SPMWorkflowSummary } from 'types/SPM';

const apiUrl = '/api/v1/spm/approvals/';
const workflowsApiUrl = '/api/v1/spm/workflows/';

interface SPMApprovalsListResponse {
  data: SPMApproval[];
  total_count: number;
}

interface SPMWorkflowsListResponse {
  data: SPMWorkflowSummary[];
  total_count: number;
}

const listSPMApprovals = () => getRequest<null, SPMApprovalsListResponse>(apiUrl);

const spmApprovalsKeys = {
  base: () => ['spm-approvals'] as const,
  list: () => [...spmApprovalsKeys.base(), 'list'] as const,
  detail: (id: string) => [...spmApprovalsKeys.base(), 'detail', id] as const,
  approvalGroups: (objectId: string) => [...spmApprovalsKeys.base(), 'approval-groups', objectId] as const,
  workflows: () => [...spmApprovalsKeys.base(), 'workflows'] as const,
};

const useListSPMApprovalsQuery = (options: UseQueryOptions<SPMApprovalsListResponse, APIError, SPMApproval[]> = {}) => {
  const QUERY_KEY = spmApprovalsKeys.list();
  const queryClient = useQueryClient();

  const query = useQuery<SPMApprovalsListResponse, APIError, SPMApproval[]>(QUERY_KEY, () => listSPMApprovals(), {
    select: (response) => response.data || [],
    ...options,
  });

  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

// Get single approval by ID
const getApproval = (approvalId: string) => getRequest<null, SPMApproval>(`${apiUrl}${approvalId}/`);

const useGetApprovalQuery = (approvalId: string | undefined, options: UseQueryOptions<SPMApproval, APIError> = {}) => {
  const QUERY_KEY = spmApprovalsKeys.detail(approvalId ?? '');

  return useQuery<SPMApproval, APIError>(QUERY_KEY, () => getApproval(approvalId as string), options);
};

// Approve/reject uses the general approval-tasks endpoint
const approvalTasksApiUrl = '/api/v1/approval-tasks/';

type ApprovalUpdateRequest = {
  is_approved: boolean;
  message?: string | null;
};

const updateApprovalTask = (csrfToken: string, approvalId: string) => (data: ApprovalUpdateRequest) =>
  putRequest<ApprovalUpdateRequest, SPMApproval>(`${approvalTasksApiUrl}${approvalId}/`, data, csrfToken);

const useUpdateApprovalMutation = (
  approvalId: string,
  options: UseMutationOptions<SPMApproval, APIError, ApprovalUpdateRequest> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<SPMApproval, APIError, ApprovalUpdateRequest>(updateApprovalTask(csrfToken, approvalId), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(spmApprovalsKeys.base());
      // Submitting writes the change logs the activity feed reads, so its corner indicators are stale.
      queryClient.invalidateQueries(spmActivityKeys.base());
      options.onSuccess?.(data, variables, context);
    },
  });
};

// Override approvals via the approval-overrides bulk-create endpoint
const approvalOverridesApiUrl = '/api/v1/approval-overrides/';

type ApprovalOverrideBulkCreateRequest = {
  approval_ids: string[];
  is_approved: boolean;
  message: string;
};

const bulkCreateApprovalOverrides = (csrfToken: string) => (data: ApprovalOverrideBulkCreateRequest) =>
  postRequest<ApprovalOverrideBulkCreateRequest, unknown>(`${approvalOverridesApiUrl}bulk-create/`, data, csrfToken);

const useCreateApprovalOverrideMutation = (
  options: UseMutationOptions<unknown, APIError, ApprovalOverrideBulkCreateRequest> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<unknown, APIError, ApprovalOverrideBulkCreateRequest>(bulkCreateApprovalOverrides(csrfToken), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(spmApprovalsKeys.base());
      queryClient.invalidateQueries(spmActivityKeys.base());
      options.onSuccess?.(data, variables, context);
    },
  });
};

// Approval activity feed — fetches approval groups for a given object
const approvalGroupsFeedUrl = '/api/v1/approval-groups/feed/';

const getApprovalGroupsForObject = (objectId: string) =>
  getRequest<null, APIList<ApprovalGroupDetail>>(
    `${approvalGroupsFeedUrl}?object_id=${objectId}&object_type=spm_proposed_change_group`,
  );

const useApprovalGroupsQuery = (
  objectId: string | undefined,
  options: UseQueryOptions<APIList<ApprovalGroupDetail>, APIError> = {},
) => {
  return useQuery<APIList<ApprovalGroupDetail>, APIError>(
    spmApprovalsKeys.approvalGroups(objectId ?? ''),
    () => getApprovalGroupsForObject(objectId as string),
    options,
  );
};

// Apply proposed changes via the workflow endpoint
type ApplyChangesResponse = {
  status: string;
  total_applied: number;
  affected_objects: number;
};

const applyProposedChanges = (csrfToken: string, workflowId: string) => () =>
  postRequest<Record<string, never>, ApplyChangesResponse>(
    `${workflowsApiUrl}${workflowId}/apply-changes/`,
    {},
    csrfToken,
  );

const useApplyProposedChangesMutation = (
  workflowId: string | undefined,
  options: UseMutationOptions<ApplyChangesResponse, APIError, void> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<ApplyChangesResponse, APIError, void>(applyProposedChanges(csrfToken, workflowId ?? ''), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(spmApprovalsKeys.base());
      options.onSuccess?.(data, variables, context);
    },
  });
};

// Admin workflows list
const listSPMWorkflows = () => getRequest<null, SPMWorkflowsListResponse>(workflowsApiUrl);

const useListSPMWorkflowsQuery = (
  options: UseQueryOptions<SPMWorkflowsListResponse, APIError, SPMWorkflowSummary[]> = {},
) => {
  const QUERY_KEY = spmApprovalsKeys.workflows();

  const query = useQuery<SPMWorkflowsListResponse, APIError, SPMWorkflowSummary[]>(
    QUERY_KEY,
    () => listSPMWorkflows(),
    {
      select: (response) => response.data || [],
      ...options,
    },
  );

  return { query };
};

export const SPMApprovalsService = {
  apiUrl,
  workflowsApiUrl,
  listSPMApprovals,
  getApproval,
  spmApprovalsKeys,
  useListSPMApprovalsQuery,
  useGetApprovalQuery,
  useUpdateApprovalMutation,
  useCreateApprovalOverrideMutation,
  useApprovalGroupsQuery,
  useApplyProposedChangesMutation,
  useListSPMWorkflowsQuery,
};
